import React, { useState } from 'react'
import { AgentRes } from '@/api/types/agent.type'
import { Popover, PopoverContent, PopoverTrigger } from '@you/ui'
import { Dialog, DialogContent, DialogHeader, DialogFooter } from '@you/ui'
import { Button } from '@you/ui'
import defaultAvatar from '@/assets/images/avatar.png'
import { useNavigate } from 'react-router-dom'
import { formatDateTime } from '@/lib/utils'

// 智能体数据接口 - 基于 AgentRes
interface AgentItemProps extends AgentRes {
  status: 'published' | 'unpublished'
  onClick?: (agent: AgentRes) => void
  onEdit?: (agent: AgentRes) => void
  onDelete?: (agentId: string | number) => void
}

const AgentItem: React.FC<AgentItemProps> = ({
  id,
  agentId,
  name,
  icon,
  description,
  creator,
  updateTime,
  isUpdated,
  workspaceId,
  workspaceName,
  status,
  onClick,
  onEdit,
  onDelete,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const navigate = useNavigate()

  // 获取状态标签样式
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'published':
        return 'bg-[#E0FFE6] text-[#2DBA53]'
      case 'unpublished':
        return 'bg-[#FFF3EA] text-[#BA842D]'
      default:
        return 'bg-[#EFEFEF] text-[#888888]'
    }
  }

  // 获取标签样式
  const getTagStyle = (index: number) => {
    const styles = [
      'bg-[#EFEFEF] text-[#888888]',
      'bg-[#EDF1FF] text-[#1E53FF]',
    ]
    return styles[index % styles.length]
  }

  // 处理删除智能体
  const handleDeleteAgent = () => {
    onDelete?.(agentId || '')
    setIsDeleteDialogOpen(false)
  }

  return (
    <div
      className="w-full h-[180px] rounded-lg border border-[#F0E9FF] bg-[#FAFAFF] p-4 cursor-pointer hover:shadow-md transition-shadow duration-200 relative"
      onClick={() =>
        onClick?.({
          id,
          agentId,
          name,
          icon,
          description,
          creator,
          workspaceId,
          status,
        } as AgentRes)
      }
    >
      {/* 右上角三个点按钮 */}
      <div
        className="absolute top-2 right-2 z-10"
        onClick={(e) => e.stopPropagation()}
      >
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded transition-colors"
              title="更多操作"
            >
              <div className="flex space-x-0.5">
                <div className="w-1 h-1 bg-[#8B76F3] rounded-full"></div>
                <div className="w-1 h-1 bg-[#8B76F3] rounded-full"></div>
                <div className="w-1 h-1 bg-[#8B76F3] rounded-full"></div>
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-32 p-1"
            align="end"
            side="bottom"
            sideOffset={4}
          >
            <div className="space-y-1">
              <button
                type="button"
                className="w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded text-left transition-colors"
                onClick={() => {
                  onEdit?.({
                    id,
                    agentId,
                    name,
                    icon,
                    description,
                    creator,
                    workspaceId,
                    status,
                  } as AgentRes)
                  setIsPopoverOpen(false)
                }}
              >
                编辑
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded text-left transition-colors"
                onClick={() => {
                  navigate(`/agents/publish?agentId=${agentId}`)
                }}
              >
                发布
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded text-left transition-colors"
                onClick={() => {
                  setIsPopoverOpen(false)
                  setIsDeleteDialogOpen(true)
                }}
              >
                删除
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex space-x-4 h-full">
        {/* 左侧头像 */}
        <div className="flex-shrink-0">
          <img
            src={icon || defaultAvatar}
            alt={name}
            className="w-12 h-12 rounded-[14px] object-cover"
          />
        </div>

        {/* 右侧内容 */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* 智能体名称 */}
          <h3 className="text-[16px] font-[500] text-[#3E2C68] mb-2 truncate">
            {name}
          </h3>

          {/* 标签列表 */}
          <div className="flex flex-wrap gap-1 mb-2">
            {/* 创建人 */}
            <span
              className={`px-2 py-1 rounded text-[10px] h-4 flex items-center ${getTagStyle(
                0
              )}`}
            >
              {creator}
            </span>
            {/* 更新人 */}
            <span
              className={`px-2 py-1 rounded text-[10px] h-4 flex items-center ${getTagStyle(
                0
              )}`}
            >
              {workspaceName}
            </span>
            {/* 状态 */}
            <span
              className={`px-2 py-1 rounded text-[10px] h-4 flex items-center ${getStatusStyle(
                status
              )}`}
            >
              {status === 'published' ? '已发布' : '未发布'}
            </span>
          </div>

          {/* 描述内容 */}
          <p className="text-[14px] text-[#AB97D7] leading-5 line-clamp-4 flex-1">
            {description}
          </p>

          {/* 右下角更新时间信息 */}
          <div className="flex justify-end mt-auto">
            <span className="text-[10px] text-[#AB97D7]">
              {isUpdated ? `${formatDateTime(updateTime || '')} 编辑` : ''}
            </span>
          </div>
        </div>
      </div>

      {/* 删除确认对话框 */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="w-[400px] p-0">
          <DialogHeader title="确认删除" />
          <div className="px-6 py-4">
            <p className="text-gray-700">
              确定要删除智能体 <span className="font-medium">"{name}"</span>{' '}
              吗？此操作不可恢复。
            </p>
          </div>
          <DialogFooter className="px-6 py-4">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              取消
            </Button>
            <Button variant="destructive" onClick={handleDeleteAgent}>
              确认删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AgentItem
